import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Book } from 'lucide-react';
import { accountingAPI } from '../services/api';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE', 'COGS'];

const AddEditAccountPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    account_number: '',
    account_name: '',
    account_type: 'EXPENSE',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    if (!isNew && id) {
      accountingAPI
        .getAccount(id)
        .then((res) => {
          const a = res.data;
          setForm({
            account_number: a.account_number || '',
            account_name: a.account_name || '',
            account_type: a.account_type || 'EXPENSE',
            description: a.description || '',
            is_active: a.is_active !== false,
          });
        })
        .catch((err) => {
          setError(err.response?.data?.detail || 'Failed to load account');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id, isNew]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        await accountingAPI.createAccount({
          account_number: form.account_number.trim(),
          account_name: form.account_name.trim(),
          account_type: form.account_type,
          description: form.description.trim() || null,
        });
        navigate('/accounting/chart-of-accounts');
      } else {
        await accountingAPI.updateAccount(id, {
          account_name: form.account_name.trim(),
          description: form.description.trim() || null,
          is_active: form.is_active,
        });
        navigate('/accounting/chart-of-accounts');
      }
    } catch (err) {
      setError(err.response?.data?.detail || (isNew ? 'Failed to create account' : 'Failed to update account'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container max-w-2xl">
      <button
        onClick={() => navigate('/accounting/chart-of-accounts')}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Chart of Accounts
      </button>

      <div className="card card-body">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-6">
          <Book className="w-6 h-6 text-amber-500" />
          {isNew ? 'Add Account' : 'Edit Account'}
        </h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {isNew && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account number *</label>
                <input
                  name="account_number"
                  value={form.account_number}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g. 1010, 5120"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account type *</label>
                <select name="account_type" value={form.account_type} onChange={handleChange} className="input-field" required>
                  {ACCOUNT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t === 'COGS' ? 'Cost of Goods Sold' : t.charAt(0) + t.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {!isNew && (
            <div className="text-sm text-slate-500">
              Account # {form.account_number} · Type: {form.account_type} (cannot be changed)
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Account name *</label>
            <input
              name="account_name"
              value={form.account_name}
              onChange={handleChange}
              className="input-field"
              placeholder="e.g. Office Supplies"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
            <input
              name="description"
              value={form.description}
              onChange={handleChange}
              className="input-field"
              placeholder="Brief description"
            />
          </div>

          {!isNew && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                className="rounded border-slate-300"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                Account is active
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : isNew ? (
                'Create account'
              ) : (
                'Save changes'
              )}
            </button>
            <button type="button" onClick={() => navigate('/accounting/chart-of-accounts')} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditAccountPage;
